import type { Prisma, Society } from "@prisma/client";
import { brokerAccountingSelector, brokerByIdSelector, brokerDetailsSelector, brokerDocumentSelect, brokersSocietiesSelector, clientSocietySelector, emailWithAttachmentsSelector, searchSocietiesSelect, societiesAbilityControlSelect, societyUserSelector } from "@dune/vars";
import { FileTypeOnSociety, HumanProcessingStatus } from "../files/files.types";
import { WarrantyType } from "../binders/binders.types";

export const howDidYouKnowDuneChoice = {
	OTHER: "Autres",
	SPONSORSHIP: "Parrainage",
	RECOMMENDATIONS: "Recommandations",
	EVENTS_TRADE_SHOWS: "Evénements / salons profesisonnels",
	WEBINARS_AND_ONLINE_EVENTS: "Webinaire et événements en ligne",
	EMAILING: "Emailing",
	SOCIAL_MEDIA: "Réseaux sociaux",
	WEBSITE: "Site internet",
};

export const PositionInTheCompanyChoice = {
	PARTNER: "Associé",
	EXECUTIVE: "Dirigeant",
	PRODUCT_MANAGER: "Responsable produit",
	ACCOUNT_MANAGER: "Chargé de compte",
	ADMINISTRATIVE_AND_FINANCIAL_MANAGER:
		"Responsable administratif et financier",
	PARTNERSHIP_MANAGER: "Responsable partenariat",
	CUSTOMER_RELATIONS_MANAGER: "Chargé de clientèle",
};

export const positionsInTheCompanyEnum = {
	PARTNER: "PARTNER",
	EXECUTIVE: "EXECUTIVE",
	PRODUCT_MANAGER: "PRODUCT_MANAGER",
	ACCOUNT_MANAGER: "ACCOUNT_MANAGER",
	ADMINISTRATIVE_AND_FINANCIAL_MANAGER: "ADMINISTRATIVE_AND_FINANCIAL_MANAGER",
	PARTNERSHIP_MANAGER: "PARTNERSHIP_MANAGER",
	CUSTOMER_RELATIONS_MANAGER: "CUSTOMER_RELATIONS_MANAGER",
} as const;

export const howDidYouKnowDuneEnum = {
	OTHER: "OTHER",
	SPONSORSHIP: "SPONSORSHIP",
	RECOMMENDATIONS: "RECOMMENDATIONS",
	EVENTS_TRADE_SHOWS: "EVENTS_TRADE_SHOWS",
	WEBINARS_AND_ONLINE_EVENTS: "WEBINARS_AND_ONLINE_EVENTS",
	EMAILING: "EMAILING",
	SOCIAL_MEDIA: "SOCIAL_MEDIA",
	WEBSITE: "WEBSITE",
} as const;

export const howDidYouKnowDune = [
	"OTHER",
	"SPONSORSHIP",
	"RECOMMENDATIONS",
	"EVENTS_TRADE_SHOWS",
	"WEBINARS_AND_ONLINE_EVENTS",
	"EMAILING",
	"SOCIAL_MEDIA",
	"WEBSITE",
] as const;

export type HowDidYouKnowDune = (typeof howDidYouKnowDune)[number];

export const positionsInTheCompany = [
	"PARTNER",
	"EXECUTIVE",
	"PRODUCT_MANAGER",
	"ACCOUNT_MANAGER",
	"ADMINISTRATIVE_AND_FINANCIAL_MANAGER",
	"PARTNERSHIP_MANAGER",
	"CUSTOMER_RELATIONS_MANAGER",
] as const;
export type PositionInTheCompany = (typeof positionsInTheCompany)[number];

export type FullSociety = Society;

export type ClientSociety = Prisma.SocietyGetPayload<{
	select: typeof clientSocietySelector;
}>;

export type SocietyAbilityControl = Prisma.SocietyGetPayload<{
	select: typeof societiesAbilityControlSelect;
}>;

export type FilesForSociety = {
	name: string;
	type: string;
	sizeInBytes: number;
	bucket: string;
	key: string;
};

export type FinalizeBroker = {
	user: {
		firstname: string;
		lastname: string;
		email: string;
		landlinePhone?: string;
		mobilePhone: string;
		position: PositionInTheCompany;
		howDidYouKnowDune: HowDidYouKnowDune;
		hasSubscribedToNewsletter: boolean;
		isSignatory: boolean;
		lastnameSignatory?: string;
		firstnameSignatory?: string;
		emailSignatory?: string;
		mobilePhoneSignatory?: string;
		landlinePhoneSignatory?: string;
		positionSignatory?: PositionInTheCompany;
	};
	society: {
		kbis: FilesForSociety;
		identity: FilesForSociety;
		rib: FilesForSociety;
		rc: FilesForSociety;
		guarantee?: FilesForSociety;
	};
};

export type FinalizePartnershipRequest = {
	user: {
		id: number;
		lastname: string;
		firstname: string;
		email: string;
		position: PositionInTheCompany;
		landlinePhone?: string;
		phone: string;
		isSignatory: boolean;
		howDidYouKnowDune: HowDidYouKnowDune;
		hasSubscribedToNewsletter: boolean;
		// Signatory data (when isSignatory is false)
		lastnameSignatory?: string;
		firstnameSignatory?: string;
		emailSignatory?: string;
		positionSignatory?: PositionInTheCompany;
		landlinePhoneSignatory?: string;
		phoneSignatory?: string;
	};
	society: {
		id: number;
		oriasNumber: string;
		siren: string;
		denomination: string;
		headquartersAddress: {
			streetNumber?: string;
			streetName?: string;
			postalCode?: string;
			city?: string;
		};
		categories: {
			category: string;
			status: "REGISTERED" | "NOT_REGISTERED";
			registrationDate: string;
			categoryDeletionDate?: string;
			fundCollection: boolean;
			mandants?: string;
		}[];
		accessibleProducts: {
			product: string;
			authorized: boolean;
			commission: string;
			minCommission: string;
			maxCommission: string;
			isReunionDepartmentAuthorized: boolean;
			binderId: number | undefined;
			warrantyType: WarrantyType | undefined;
			gfaProduct: boolean;
		}[];
	};
	banking: {
		iban: string;
		bic: string;
	};
	insurance: {
		insurer: string;
		validityStartDate: Date | undefined;
		validityEndDate: Date | undefined;
	};
	files: {
		file: {
			id: string;
			name: string;
			key: string;
			mimeType: string;
			humanProcessingStatus: HumanProcessingStatus;
			bucket: string;
		};
		type: FileTypeOnSociety;
	}[];
};

export type SearchSocietiesPayload = Prisma.SocietyGetPayload<{
	select: typeof searchSocietiesSelect;
}>;

export interface HeadquartersAddressPayload {
	streetNumber?: string;
	streetName?: string;
	postalCode?: string;
	city?: string;
}

export interface EditBrokerDetailsPayload {
	siren?: string;
	denomination?: string;
	headquartersAddress?: HeadquartersAddressPayload;
	entityList: string[];
	attachedToANetwork?: "true" | "false";
	networkName?: string;
	attachedToAGroup?: "true" | "false";
	sirenGroup?: string;
	companyGroupName?: string;
	oriasNumber?: string;
	executiveUserLastname?: string;
	executiveUserFirstname?: string;
	executiveUserEmail?: string;
	executiveUserPhone?: string;
	graphicCharter?: {
		logo?: {
			name: string;
			key: string;
			bucket: string;
			mimeType: string;
			url: string;
		};
		color?: string;
	};
	canGenerateApiKeys?: boolean;
}

export interface BulkReassignBrokerProjectsPayload {
	newBrokerSocietyId: number;
	constructionSiteIds: number[];
}

export interface BulkReassignBrokerProjectsResult {
	updatedConstructionSitesCount: number;
	updatedPropositionsCount: number;
}

export type BrokerDocument = Prisma.FilesOnSocietyGetPayload<{
	select: typeof brokerDocumentSelect;
}>;

export type BrokerDocumentType = FileTypeOnSociety;

export const brokerDocumentTypeLabels: Record<FileTypeOnSociety, string> = {
	KBIS: "Kbis",
	IDENTITY: "Pièce d'identité",
	RIB: "RIB",
	RC: "Attestation RC Professionnelle",
	GUARANTEE: "Attestation Garantie Financière",
	ORIAS: "Attestation ORIAS",
	LCB: "LCB-FT",
	LOGO: "Logo",
	CONVENTION: "Convention d'apporteur",
	CONVENTION_SIGNED: "Convention d'apporteur signée",
	OTHER: "Autre",
};

export type SocietyUserVar = Prisma.UserGetPayload<{
	select: typeof societyUserSelector;
}>;

export type UpdateEmailArchivedPayload = {
	brokerId: number;
	emailId: number;
	isArchived: boolean;
};

export interface ImportEmailAttachmentPayload {
	brokerId: number;
	emailAttachmentId: number;
}

export interface ImportEmailAttachmentsResponse {
	success: boolean;
}

export type EmailWithAttachments = Prisma.EmailRelatedEntityGetPayload<{
	select: typeof emailWithAttachmentsSelector
}>;

export type BrokersSocietiesSelect = Prisma.SocietyGetPayload<{
	select: typeof brokersSocietiesSelector
}>;

export type BrokerByIdSelect = Prisma.SocietyGetPayload<{
	select: typeof brokerByIdSelector
}>;

export type BrokerAccountingSelect = Prisma.SocietyGetPayload<{
	select: typeof brokerAccountingSelector;
}>; 

export type BrokerDetailsPayload = Prisma.UserGetPayload<{
	select: typeof brokerDetailsSelector
}>;

export interface GetBrokersSocietiesQueryParams {
	page?: number;
	limit?: number;
	search?: string;
	status?: string;
}
